import { CademyError } from '@shared/domain-shared';
import { ReactElement, useCallback, useMemo, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

type GetCaptchaToken = () => Promise<string>;

export type useCaptchaResult = [ReactElement, GetCaptchaToken];

class MissingCaptchaError extends CademyError {
    constructor() {
        super(
            'captcha/failed-to-generate',
            'Failed to generate captcha token',
            'We were unable to generate a captcha token for this submission',
            500
        );
    }
}

export const useCaptcha = (): useCaptchaResult => {
    const recaptchaRef = useRef<ReCAPTCHA>(null);

    const captchaElement = useMemo(() => {
        if (!process.env.NEXT_PUBLIC_RECAPTCHA_KEY) {
            return <></>;
        }
        return (
            <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_KEY}
            />
        );
    }, []);

    const getCaptchaToken = useCallback(async () => {
        if (!recaptchaRef.current) {
            throw new MissingCaptchaError();
        }
        const token = await recaptchaRef.current.executeAsync();
        if (!token) {
            throw new MissingCaptchaError();
        }
        return token;
    }, []);

    return [captchaElement, getCaptchaToken];
};
